import { AlertCircle, Inbox } from "lucide-react";
import { useCallback, useState } from "react";

import { Button } from "@/common/ui/button";

import { AddEmailForm } from "./AddEmailForm";
import { DeleteDialog } from "./DeleteDialog";
import { StatsCards } from "./StatsCards";
import type { WaitlistSignup } from "./types";
import { useWaitlist } from "./use-waitlist";
import { useWaitlistStats } from "./use-waitlist-stats";
import { WaitlistTable } from "./WaitlistTable";
import { WaitlistToolbar } from "./WaitlistToolbar";

export function WaitlistPage(): React.ReactElement {
  const {
    signups,
    isLoading,
    error,
    refresh,
    addEmail,
    deleteSignup,
    sortDirection,
    toggleSort,
    lastRefreshedAt,
  } = useWaitlist();

  const { stats, isLoading: statsLoading, refresh: refreshStats } = useWaitlistStats();

  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WaitlistSignup | null>(null);

  const handleRefresh = useCallback(() => {
    refresh();
    refreshStats();
  }, [refresh, refreshStats]);

  const handleAdd = useCallback(
    async (email: string): Promise<{ success: boolean; message: string }> => {
      const result = await addEmail(email);
      if (result.success) {
        refreshStats();
      }
      return result;
    },
    [addEmail, refreshStats]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteSignup(deleteTarget.id);
      refreshStats();
    } catch {
      // Error handled by hook
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteSignup, refreshStats]);

  return (
    <div data-testid="waitlist-page" className="space-y-6">
      <WaitlistToolbar
        isLoading={isLoading}
        lastRefreshedAt={lastRefreshedAt}
        onRefresh={handleRefresh}
        showAddForm={showAddForm}
        onToggleAddForm={() => setShowAddForm((prev) => !prev)}
      />

      {showAddForm && <AddEmailForm onAdd={handleAdd} onCancel={() => setShowAddForm(false)} />}

      <StatsCards stats={stats} isLoading={statsLoading} />

      {error && (
        <div
          data-testid="waitlist-error"
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <Button
            data-testid="retry-button"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={handleRefresh}
          >
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !error && signups.length === 0 && (
        <div
          data-testid="waitlist-empty"
          className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground"
        >
          <Inbox className="h-12 w-12" />
          <p className="text-lg font-medium">No signups yet</p>
          <p className="text-sm">Waitlist signups will appear here.</p>
        </div>
      )}

      {signups.length > 0 && (
        <WaitlistTable
          signups={signups}
          sortDirection={sortDirection}
          onToggleSort={toggleSort}
          onDelete={setDeleteTarget}
        />
      )}

      <DeleteDialog
        signup={deleteTarget}
        open={deleteTarget !== null}
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
